import React, {CSSProperties, useEffect, useRef, useState} from "react";
import {Button, Form, Input, Select} from "antd";
import {request} from "../../util/request";
import {GLOBAL_STANDARD_MAP_API} from "../../config/api";
import {ObjTest} from "jzfz-web-utils";
import {FormInstance} from "antd/lib/form/hooks/useForm";
import {ModuleEnum} from "../../config/constant";


const { Option } = Select;

// 字段名称枚举
enum FieldNameEnum {
    customMap = 'customMap',
    customMapPageCode = 'customMapPageCode',
    customMapPageIndex = 'customMapPageIndex',
}


interface StandardMapInputProps {
    className?: string;
    style?: CSSProperties;
    moduleType?: ModuleEnum;    // 模块类型
    form: FormInstance;
    onChange?: (value: any) => void;
}


/**
 * 标准图集输入组件
 * @param Props
 * @constructor
 */
const StandardMapSelect = (props: StandardMapInputProps) => {
    const { className, style, moduleType, form, onChange = (value: any) => {} } = props;
    // 图集
    const [mapList, setMapList] = useState<any>([]);
    const [mapListLoading, setMapListLoading] = useState<boolean>(false);
    // 图集编号
    const [pageList, setPageList] = useState<string[]>([]);
    const [pageListLoading, setPageListLoading] = useState<boolean>(false);
    // 定时器
    const timer = useRef<any>(null);
    const { getFieldsValue, getFieldValue, resetFields } = form;


    useEffect(() => {
        return () => {
            clearTimeout(timer.current);
        }
    }, [])


    /**
     * 搜索图集
     * @param value
     */
    const searchHandle = (value: string) => {
        // 防抖
        if (timer.current) {
            clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
            setMapListLoading(true);
            request('/map/list/query', {keyword: value}, {method: 'get', baseURL: GLOBAL_STANDARD_MAP_API}).then((res: any) => {
                setMapList(res.data);
            }).finally(() => {
                setMapListLoading(false);
            })
        }, 600)
    }


    /**
     * 过滤图集
     * @param list
     */
    const filter = (list: any[]): any[] => {
        if (moduleType !== ModuleEnum.standardMap) {
            return list;
        }
        const { mapPagePkId } = getFieldsValue();
        // 不能添加该图集本身
        return list.filter((v: any) => v.pkId !== mapPagePkId);
    }


    /**
     * 选择图集
     * @param value
     */
    const mapSelectHandle = (value: string) => {
        setPageListLoading(true);
        request('/map-page/list/queryByMapId', {mapId: value}, {method: 'get', baseURL: GLOBAL_STANDARD_MAP_API}).then((res: any) => {
            resetFields([FieldNameEnum.customMapPageCode, FieldNameEnum.customMapPageIndex]);
            setPageList(filter(res.data || []));
        }).finally(() => {
            setPageListLoading(false);
        })
    }


    /**
     * 清除图集
     */
    const mapClearHandle = () => {
        resetFields([FieldNameEnum.customMapPageCode, FieldNameEnum.customMapPageIndex]);
        setPageList([]);
    }


    /**
     * 选择图集编号
     */
    const pageSelectHandle = () => {
        resetFields([FieldNameEnum.customMapPageIndex]);
    }


    /**
     * 清除图集编号
     */
    const pageClearHandle = () => {
        resetFields([FieldNameEnum.customMapPageIndex]);
    }


    /**
     * 添加
     */
    const addHandle = () => {
        let data = getFieldsValue();
        const { mapNumber, mapName } = mapList.find((v: any) => v.mapPkId === data[FieldNameEnum.customMap]) || {};
        let target = pageList.filter((v: any) => data[FieldNameEnum.customMapPageCode].includes(v.pkId));
        let values = target.map((v: any) => ({
            mapCode: mapNumber,
            mapName,
            pkid: v.pkId,
            mapPage: v.pageCode,
            fullNamePath: v.fullNamePath,
            figureNumber: data[FieldNameEnum.customMapPageIndex],
        }))
        resetFields([FieldNameEnum.customMap, FieldNameEnum.customMapPageCode, FieldNameEnum.customMapPageIndex]);
        onChange(values);
    }


    return (
        <div className={[className, 'flexic'].join(' ')} style={style}>
            <Form.Item name={FieldNameEnum.customMap} noStyle>
                <Select
                    className="standard-map-form-input-name"
                    loading={mapListLoading}
                    showSearch
                    filterOption={false}
                    dropdownMatchSelectWidth={false}
                    onSearch={searchHandle}
                    onSelect={mapSelectHandle}
                    onClear={mapClearHandle}
                >
                    {
                        mapList.map((v: any, i: number) => (
                            <Option key={i} value={v.mapPkId}>{v.mapNumber}{v.mapName}</Option>
                        ))
                    }
                </Select>
            </Form.Item>
            <span className="dashes-line"></span>
            <Form.Item shouldUpdate noStyle>
                {() => (
                    <Form.Item name={FieldNameEnum.customMapPageCode} initialValue={[]} noStyle>
                        <Select
                            className="standard-map-form-input-page"
                            allowClear
                            showSearch
                            loading={pageListLoading}
                            mode="multiple"
                            disabled={mapListLoading || !getFieldValue(FieldNameEnum.customMap)}
                            optionFilterProp="label"
                            filterOption={(inputValue: string, option: any) => {
                                const { label } = option;
                                return (label || '').trim().startsWith(inputValue);
                            }}
                            onSelect={pageSelectHandle}
                            onClear={pageClearHandle}
                        >
                            {
                                pageList.map((v: any, i: number) => (
                                    <Option key={i} value={v.pkId} label={v.pageCode}>{v.pageCode}</Option>
                                ))
                            }
                        </Select>
                    </Form.Item>
                )}
            </Form.Item>
            <span className="dashes-line"></span>
            <Form.Item shouldUpdate noStyle>
                {() => (
                    <Form.Item name={FieldNameEnum.customMapPageIndex} noStyle>
                        <Input
                            className="standard-map-form-input-index"
                            disabled={
                                (getFieldValue(FieldNameEnum.customMapPageCode) || []).length === 0
                                || (getFieldValue(FieldNameEnum.customMapPageCode) || []).length > 1
                            }
                            placeholder="定位页内具体索引编号，没有可不填"
                        />
                    </Form.Item>
                )}
            </Form.Item>
            <Form.Item shouldUpdate noStyle>
                {() => (
                    <Button
                        className="confirm-add-btn"
                        type="primary"
                        disabled={!ObjTest.isNotEmptyArray(getFieldValue(FieldNameEnum.customMapPageCode))}
                        onClick={addHandle}
                    >确认添加</Button>
                )}
            </Form.Item>
        </div>
    )
}


export default StandardMapSelect;
